import { useState, useMemo } from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Link, router } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { TestCard, type TestItem } from '@/components/student/TestCard'
import { SyllabusTreeModal } from '@/components/student/SyllabusTreeModal'
import {
  ArrowLeft, Target, HelpCircle, BookOpen,
  Flame, Crown, CheckCircle2, RotateCcw, Search, X, Award, FileText
} from 'lucide-react'

interface Props {
  exam: { id: number; name: string; code: string; year: number | null }
  enrolled: boolean
  tests: TestItem[]
  credit_balance: number
}

type FilterTab = 'all' | 'new_hot' | 'premium' | 'unlocked' | 'attempted'

export default function StudentExamsShow({ exam, enrolled, tests = [], credit_balance }: Props) {
  const [search, setSearch] = useState('')
  const [activeTab, setActiveTab] = useState<FilterTab>('all')
  const [selectedSyllabusTest, setSelectedSyllabusTest] = useState<TestItem | null>(null)
  const [syllabusModalOpen, setSyllabusModalOpen] = useState(false)

  const handleOpenSyllabus = (test: TestItem) => {
    setSelectedSyllabusTest(test)
    setSyllabusModalOpen(true)
  }

  // Categorized test collections
  const attemptedTests = useMemo(() => tests.filter((t) => t.attempt_data?.has_attempted), [tests])
  const unattemptedTests = useMemo(() => tests.filter((t) => !t.attempt_data?.has_attempted), [tests])
  // Quick stats
  const unlockedCount = useMemo(() => tests.filter((t) => t.has_access || t.is_free).length, [tests])
  const attemptedCount = attemptedTests.length
  const premiumCount = useMemo(() => tests.filter((t) => !t.has_access && !t.is_free).length, [tests])

  // Filtered test items for specific tab or active search
  const filteredTests = useMemo(() => {
    return tests.filter((test) => {
      const matchesSearch =
        test.title.toLowerCase().includes(search.toLowerCase()) ||
        (test.description && test.description.toLowerCase().includes(search.toLowerCase())) ||
        test.test_type.toLowerCase().includes(search.toLowerCase())

      if (!matchesSearch) return false

      if (activeTab === 'new_hot') {
        return !test.attempt_data?.has_attempted
      }
      if (activeTab === 'premium') {
        return !test.has_access && !test.is_free
      }
      if (activeTab === 'unlocked') {
        return (test.has_access || test.is_free) && !test.attempt_data?.has_attempted
      }
      if (activeTab === 'attempted') {
        return test.attempt_data?.has_attempted
      }
      return true
    })
  }, [tests, search, activeTab])

  const handleEnroll = () => {
    router.post(`/student/exams/${exam.id}/enroll`, {}, { preserveScroll: true })
  }

  return (
    <StudentShell>
      <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Top Navigation */}
        <div className="flex items-center justify-between gap-3 pt-0.5">
          <Button
            variant="outline"
            size="sm"
            asChild
            className="rounded-xl border-border/80 bg-card text-foreground hover:bg-muted/40 transition-all shadow-2xs h-8 sm:h-9 px-2.5 sm:px-3.5"
          >
            <Link href="/student/exams">
              <ArrowLeft className="h-4 w-4 text-muted-foreground sm:mr-2 shrink-0" />
              <span className="hidden sm:inline">Back to Exams</span>
            </Link>
          </Button>

          {enrolled ? (
            <span className="text-[11px] font-bold text-primary bg-primary/10 border border-primary/25 rounded-full px-3 py-1 inline-flex items-center gap-1.5 shadow-2xs">
              <CheckCircle2 className="h-3.5 w-3.5" />
              <span>Enrolled</span>
            </span>
          ) : (
            <Button
              size="sm"
              onClick={handleEnroll}
              className="rounded-xl text-xs font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all h-8 sm:h-9 px-3 sm:px-4 cursor-pointer shadow-xs"
            >
              <span className="sm:hidden">Enroll Free</span>
              <span className="hidden sm:inline">Enroll Now (Free)</span>
            </Button>
          )}
        </div>

        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-3.5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 sm:gap-6">
            <div className="space-y-2 sm:space-y-2.5 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/15 px-2.5 py-0.5 rounded-full border border-primary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Target className="h-3 w-3 text-primary" />
                  <span>Target Exam</span>
                </span>
              </div>

              <h1 className="text-xl sm:text-2xl font-black text-foreground tracking-tight">
                {exam.name}
              </h1>

              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground font-medium pt-0.5">
                {exam.year && (
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-3.5 w-3.5 text-primary shrink-0" />
                    <span>Exam Year: {exam.year}</span>
                  </span>
                )}
                {exam.year && <span className="text-border">•</span>}
                <span className="flex items-center gap-1">
                  <Award className="h-3.5 w-3.5 text-primary shrink-0" />
                  <span>{tests.length} Active Mock {tests.length === 1 ? 'Test' : 'Tests'}</span>
                </span>
              </div>
            </div>

            {/* Quick Metrics Strip — Vibrant Semantic Token Cards */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3.5 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-4 rounded-xl sm:rounded-2xl bg-primary/10 border border-primary/25 text-center sm:text-left transition-all hover:bg-primary/15">
                <span className="text-lg sm:text-2xl font-black text-primary tracking-tight">{tests.length}</span>
                <span className="text-[9px] sm:text-[10px] font-bold text-primary uppercase tracking-wider mt-0.5 whitespace-nowrap">
                  Tests
                </span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-4 rounded-xl sm:rounded-2xl bg-secondary/10 border border-secondary/25 text-center sm:text-left transition-all hover:bg-secondary/15">
                <span className="text-lg sm:text-2xl font-black text-secondary tracking-tight">{attemptedCount}</span>
                <span className="text-[9px] sm:text-[10px] font-bold text-secondary uppercase tracking-wider mt-0.5 whitespace-nowrap">
                  Attempted
                </span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-4 rounded-xl sm:rounded-2xl bg-tertiary/10 border border-tertiary/25 text-center sm:text-left transition-all hover:bg-tertiary/15">
                <span className="text-lg sm:text-2xl font-black text-tertiary tracking-tight">{credit_balance}</span>
                <span className="text-[9px] sm:text-[10px] font-bold text-tertiary uppercase tracking-wider mt-0.5 whitespace-nowrap">
                  Credits
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Filter & Search Bar */}
        {tests.length > 0 && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-4">
            {/* Tabs */}
            <div className="flex items-center p-1 bg-muted/50 rounded-2xl border border-border/60 overflow-x-auto shrink-0 shadow-2xs [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden touch-pan-x">
              <button
                type="button"
                onClick={() => setActiveTab('all')}
                className={`px-3 py-1.5 sm:px-3.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 whitespace-nowrap ${
                  activeTab === 'all'
                    ? 'bg-card text-foreground shadow-xs border border-border/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span>All Mock Tests</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'all' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {tests.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('new_hot')}
                className={`px-3 py-1.5 sm:px-3.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 whitespace-nowrap ${
                  activeTab === 'new_hot'
                    ? 'bg-card text-foreground shadow-xs border border-border/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Flame className="h-3.5 w-3.5 text-destructive fill-destructive" />
                <span>Hot & New</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'new_hot' ? 'bg-destructive/15 text-destructive' : 'bg-muted text-muted-foreground'}`}>
                  {unattemptedTests.length}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('premium')}
                className={`px-3 py-1.5 sm:px-3.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 whitespace-nowrap ${
                  activeTab === 'premium'
                    ? 'bg-card text-foreground shadow-xs border border-border/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Crown className="h-3.5 w-3.5 text-tertiary" />
                <span>Premium</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'premium' ? 'bg-tertiary/15 text-tertiary' : 'bg-muted text-muted-foreground'}`}>
                  {premiumCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('unlocked')}
                className={`px-3 py-1.5 sm:px-3.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 whitespace-nowrap ${
                  activeTab === 'unlocked'
                    ? 'bg-card text-foreground shadow-xs border border-border/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
                <span>Free / Unlocked</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'unlocked' ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                  {unlockedCount}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setActiveTab('attempted')}
                className={`px-3 py-1.5 sm:px-3.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer shrink-0 whitespace-nowrap ${
                  activeTab === 'attempted'
                    ? 'bg-card text-foreground shadow-xs border border-border/40'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <RotateCcw className="h-3.5 w-3.5 text-secondary" />
                <span>Attempted</span>
                <span className={`text-[10px] px-1.5 py-0.2 rounded-md font-extrabold ${activeTab === 'attempted' ? 'bg-secondary/15 text-secondary' : 'bg-muted text-muted-foreground'}`}>
                  {attemptedCount}
                </span>
              </button>
            </div>

            {/* Search Input */}
            <div className="relative flex-1 sm:max-w-xs md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                type="text"
                placeholder="Search tests..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-8 h-9 sm:h-10 rounded-xl bg-card border-border/70 text-xs sm:text-sm focus-visible:ring-primary shadow-2xs"
              />
              {search && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setSearch('')}
                  className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 text-muted-foreground hover:text-foreground rounded-lg"
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Tests List Section */}
        {tests.length === 0 ? (
          <Card className="p-8 sm:p-14 text-center border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm rounded-3xl space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto border border-border/60 shadow-xs">
              <HelpCircle className="h-7 w-7" />
            </div>
            <div className="space-y-1 max-w-sm mx-auto">
              <h3 className="text-base sm:text-lg font-bold text-foreground">
                No Mock Tests Published Yet
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Mock tests are regularly calibrated and added for {exam.name}. Please check back soon!
              </p>
            </div>
          </Card>
        ) : filteredTests.length > 0 ? (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
              {filteredTests.map((test) => (
                <TestCard
                  key={test.id}
                  test={test}
                  onViewSyllabus={handleOpenSyllabus}
                />
              ))}
            </div>
          </div>
        ) : (
          /* Empty State */
          <Card className="p-8 sm:p-14 text-center border-border/80 bg-card/60 backdrop-blur-glass shadow-glass-sm rounded-3xl space-y-4">
            <div className="w-14 h-14 rounded-2xl bg-muted/60 text-muted-foreground flex items-center justify-center mx-auto border border-border/60 shadow-xs">
              <FileText className="h-7 w-7" />
            </div>
            <div className="space-y-1 max-w-sm mx-auto">
              <h3 className="text-base sm:text-lg font-bold text-foreground">
                {search ? 'No Matching Tests Found' : 'No Tests in this Category'}
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                {search
                  ? `We couldn't find any tests matching "${search}". Try clearing your search or switching tabs.`
                  : 'There are currently no mock tests available under this filter.'}
              </p>
            </div>
            {search && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setSearch('')}
                className="rounded-xl border-border hover:bg-muted text-xs font-semibold"
              >
                Clear Search
              </Button>
            )}
          </Card>
        )}

        {/* Syllabus Tree Modal */}
        <SyllabusTreeModal
          test={selectedSyllabusTest}
          open={syllabusModalOpen}
          onOpenChange={setSyllabusModalOpen}
        />
      </div>
    </StudentShell>
  )
}
